/**
 * Admin screens for the venue leaderboard (relay/leaderboard.ts): create a period with its locked
 * rules and limits, review its games with warnings and hide or restore any, and unlock or reset a
 * player's PIN. Reached from Admin → Leaderboard. Pure helpers live in ../leaderboardAdmin.ts.
 */

import {
  AdminAuthError,
  addLeaderboardPlayer,
  approveTable,
  createLeaderboardPeriod,
  createTvLinkApi,
  declineTable,
  fetchDisplayAdmin,
  fetchSeasons,
  removeTvLinkApi,
  saveDisplayAdmin,
  setSeasonFinal,
  type DisplayAdminData,
  type SeasonItem,
  fetchTableRequests,
  deleteLeaderboardPeriod,
  fetchLeaderboardPeriods,
  fetchLeaderboardPlayers,
  fetchStandings,
  fetchPeriodReview,
  fetchVenues,
  getStoredAdminSecret,
  hideLeaderboardGame,
  restoreLeaderboardGame,
  storeAdminSecret,
  deleteLeaderboardPlayer,
  unlockLeaderboardPlayer,
  type AdminRole,
  type TableRequest,
  type LeaderboardPeriod,
  type LeaderboardPlayerItem,
  type PeriodReviewData,
  type StandingsBoard,
  type ReviewGameItem,
  type Settlement,
} from '../adminApi';
import { renderBrandHeader } from '../branding';
import { boardRowsHtml } from './leaderboardBoard';
import { DEFAULT_PIN } from '../leaderboardClient';
import { esc, formatDelta } from '../helpers';
import {
  SETTLEMENT_HELP,
  SETTLEMENT_LABEL,
  SETTLEMENT_MODE,
  STATUS_LABEL,
  buildPeriodRequest,
  defaultPeriodDraft,
  describeLimits,
  formatDuration,
  formatPeriodRange,
  formatPeriodTimes,
  formatTableWhen,
  sortReviewGames,
  type PeriodDraft,
} from '../leaderboardAdmin';
import { defaultHouseRules, renderSettings } from './settings';
import { renderGuestLinkQr } from './shareGame';
import { THEMES } from '../theme';

export interface LeaderboardAdminActions {
  back(): void;
  /** Who is looking. Both see the same screen (Indra, 2026-10-09); kept so the screen can be limited again in one place. */
  role: AdminRole;
}

/** Whether a role gets the whole Leaderboard screen: periods, review, the TV look and the player tools. Today both do. */
export function fullLeaderboardAccess(_role: AdminRole): boolean {
  return true;
}

/** The new-period form's contents, kept here so it survives a trip through the House Rules editor. */
let draft: PeriodDraft | undefined;

const DEFAULT_VENUE_NAME = 'default';

export function renderLeaderboardAdmin(root: HTMLElement, actions: LeaderboardAdminActions): void {
  draft = undefined;
  void showHome(root, actions);
}

// --- shared bits ------------------------------------------------------------

function renderSecretPrompt(root: HTMLElement, actions: LeaderboardAdminActions, error: string | undefined): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Admin Secret Required</h1>
      <p class="muted">
        Separate from the branding PIN — this authorizes server-side actions. Enter it once; it's remembered on this device after that.
      </p>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      <label for="secretInput">Admin secret</label>
      <input type="password" id="secretInput" autocomplete="off" />
      <div class="row" style="margin-top:1rem">
        <button type="button" id="backBtn" class="secondary">Back</button>
        <button type="button" id="saveBtn">Continue</button>
      </div>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#saveBtn')!.addEventListener('click', () => {
    const secret = root.querySelector<HTMLInputElement>('#secretInput')!.value.trim();
    if (!secret) {
      renderSecretPrompt(root, actions, 'Enter the admin secret.');
      return;
    }
    storeAdminSecret(secret);
    void showHome(root, actions);
  });
}

function loading(root: HTMLElement, title: string): void {
  root.innerHTML = `${renderBrandHeader()}<div class="card"><h1>${esc(title)}</h1><p class="muted">Loading…</p></div>`;
}

/** Runs a screen's loader; a bad or missing admin secret goes back to the prompt, anything else to `onError`. */
async function guarded(
  root: HTMLElement,
  actions: LeaderboardAdminActions,
  load: () => Promise<void>,
  onError: (message: string) => void,
): Promise<void> {
  if (!getStoredAdminSecret()) {
    renderSecretPrompt(root, actions, undefined);
    return;
  }
  try {
    await load();
  } catch (e) {
    if (e instanceof AdminAuthError) {
      renderSecretPrompt(root, actions, 'Incorrect admin secret.');
      return;
    }
    onError(e instanceof Error ? e.message : 'Something went wrong. Check your connection.');
  }
}

const noteBox = (text: string) => `<div class="lb-note">${esc(text)}</div>`;

/** What staff are told after an unlock: the standard PIN, and that the player then chooses their own. */
const unlockedMessage = (nickname: string): string =>
  `${nickname} is unlocked. Their PIN is now ${DEFAULT_PIN} — they type it, then choose their own. It only works for a short time, so do this now.`;

// --- the standings (owner and venue staff; a TV view can be built on this later) ------------------------------------------

/** The refresh cadence of the standings screen. */
const STANDINGS_REFRESH_MS = 30_000;

function boardCards(boards: StandingsBoard[]): string {
  if (boards.length === 0) return '<p class="muted">No leaderboard is running right now.</p>';
  return boards
    .map(
      (b) => `
      <div class="standings-board">
        <h2>${esc(b.venue)}</h2>
        <p class="muted">${esc(formatPeriodRange(b.period))} · ${esc(SETTLEMENT_LABEL[b.period.settlement])}</p>
        ${
          b.entries.length === 0
            ? '<p class="muted">No games have been counted yet.</p>'
            : `<table class="summary">
                 <thead><tr><th>#</th><th>Player</th><th>Points</th><th>Games</th><th>Wins</th></tr></thead>
                 <tbody>${boardRowsHtml(b.entries, [])}</tbody>
               </table>
               <p class="muted" style="margin-top:0.5rem">Ranked by total points, then rounds won, then whoever reached that total first. Showing the top ${b.entries.length}.</p>`
        }
      </div>`,
    )
    .join('');
}

const clock = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

/** The leaderboard of every running period, refreshed by itself (Indra: both the owner and the venue admin see it; a TV can come later). */
async function showStandings(root: HTMLElement, actions: LeaderboardAdminActions): Promise<void> {
  loading(root, 'Leaderboard');
  await guarded(
    root,
    actions,
    async () => {
      const boards = await fetchStandings();
      root.innerHTML = `
        ${renderBrandHeader()}
        <div class="card" data-standings>
          <h1>Leaderboard</h1>
          <div id="standingsBoards">${boardCards(boards)}</div>
          <p class="muted" id="standingsStamp" style="margin-top:0.5rem">Updated ${esc(clock(Date.now()))} · refreshes by itself</p>
          <div class="row" style="margin-top:1rem">
            <button type="button" id="standingsBack" class="secondary">← Back</button>
            <button type="button" id="standingsRefresh">Refresh</button>
          </div>
        </div>
      `;
      const boardsBox = root.querySelector<HTMLElement>('#standingsBoards')!;
      const stamp = root.querySelector<HTMLElement>('#standingsStamp')!;
      const refresh = async () => {
        try {
          const next = await fetchStandings();
          if (!root.contains(boardsBox)) return;
          boardsBox.innerHTML = boardCards(next);
          stamp.textContent = `Updated ${clock(Date.now())} · refreshes by itself`;
        } catch {
          // No signal for a moment: keep what is on screen.
        }
      };
      const timer = setInterval(() => {
        if (!root.contains(boardsBox)) return clearInterval(timer);
        void refresh();
      }, STANDINGS_REFRESH_MS);
      root.querySelector<HTMLButtonElement>('#standingsBack')!.addEventListener('click', () => {
        clearInterval(timer);
        void showHome(root, actions);
      });
      root.querySelector<HTMLButtonElement>('#standingsRefresh')!.addEventListener('click', () => void refresh());
    },
    (message) => void showHome(root, actions, undefined, message),
  );
}

// --- home: periods and players --------------------------------------------

async function showHome(root: HTMLElement, actions: LeaderboardAdminActions, notice?: string, error?: string): Promise<void> {
  loading(root, 'Leaderboard');
  await guarded(
    root,
    actions,
    async () => {
      const [periods, locked, requests, seasons, display] = await Promise.all([
        fullLeaderboardAccess(actions.role) ? fetchLeaderboardPeriods() : Promise.resolve<LeaderboardPeriod[]>([]),
        fetchLeaderboardPlayers({ lockedOnly: true }),
        fetchTableRequests(),
        // The season results and the TV display belong to the owner and venue staff alike; if either can't load, the rest still works.
        fetchSeasons().catch(() => undefined),
        fetchDisplayAdmin().catch(() => undefined),
      ]);
      renderHome(root, actions, periods, locked, requests, notice, error, { seasons, display });
    },
    (message) => renderHome(root, actions, [], [], [], undefined, message),
  );
}

function periodCard(p: LeaderboardPeriod): string {
  const deletable = p.status === 'upcoming' && p.gamesCompleted === 0 && p.gamesHidden === 0;
  return `
    <div class="card lb-period" style="margin-top:0.5rem">
      <div><strong>${esc(p.venue)}</strong> <span class="tag">${esc(STATUS_LABEL[p.status])}</span></div>
      <div class="muted">${esc(formatPeriodTimes(p, Date.now()))}</div>
      <div class="muted">${esc(SETTLEMENT_LABEL[p.settlement])}</div>
      <div class="muted">${esc(describeLimits(p))}</div>
      <div class="muted">${p.gamesCompleted} game${p.gamesCompleted === 1 ? '' : 's'} · ${p.players} player${p.players === 1 ? '' : 's'}${
        p.gamesHidden > 0 ? ` · ${p.gamesHidden} hidden` : ''
      }</div>
      <div class="row" style="margin-top:0.5rem">
        <button type="button" class="secondary review-btn" data-id="${esc(p.id)}">Review games</button>
        ${deletable ? `<button type="button" class="ghost delete-btn" data-id="${esc(p.id)}">Delete</button>` : ''}
      </div>
    </div>`;
}

/** What the staff see for each table: its name, the venue, when it was sent, and the four User IDs in seating order. */
function tablesHtml(tables: TableRequest[], now: number): string {
  if (tables.length === 0) return '<p class="muted">No tables are waiting.</p>';
  return tables
    .map(
      (t) => `
      <div class="card lb-period" style="margin-top:0.5rem">
        <div><strong>${esc(t.tableName)}</strong> <span class="tag">${esc(t.venue)}</span></div>
        <div class="muted">Sent ${esc(formatTableWhen(t.requestedAt, now))}</div>
        <div style="margin-top:0.35rem">
          ${t.players.map((p, i) => `<div>${i + 1}. <strong>${esc(p)}</strong></div>`).join('')}
        </div>
        <div class="muted">${t.seatingMode === 'dice' ? 'Seats decided by the dice — listed in the order they joined.' : 'Listed in seating order: East, South, West, North.'}</div>
        <div class="row" style="margin-top:0.5rem">
          <button type="button" class="table-approve" data-ref="${esc(t.ref)}">Approve</button>
          <button type="button" class="ghost table-decline" data-ref="${esc(t.ref)}">Decline</button>
        </div>
      </div>`,
    )
    .join('');
}

interface HomeExtras {
  seasons?: SeasonItem[] | undefined;
  display?: DisplayAdminData | undefined;
}

/** Where a TV opens the board: this app's own address with the link's token in the hash. */
const tvAddress = (token: string): string => `${location.origin}${location.pathname}#tv=${token}`;

function seasonsHtml(seasons: SeasonItem[] | undefined): string {
  if (!seasons) return '<p class="muted">Could not load the finished seasons. Try again in a moment.</p>';
  if (seasons.length === 0) return '<p class="muted">No season has finished with results yet.</p>';
  return seasons
    .map(
      (s) => `
      <div class="card lb-period" style="margin-top:0.5rem">
        <div><strong>Season ${s.season}</strong> · ${esc(s.venue)} <span class="tag">${s.state === 'final' ? 'Final' : 'Under review'}</span></div>
        <div class="muted">${esc(formatPeriodRange(s))} · ${s.players} player${s.players === 1 ? '' : 's'}</div>
        <div class="row" style="margin-top:0.5rem">
          ${
            s.state === 'final'
              ? `<button type="button" class="ghost season-reopen" data-id="${esc(s.id)}">Back to review</button>`
              : `<button type="button" class="season-final" data-id="${esc(s.id)}">Mark as final…</button>`
          }
        </div>
      </div>`,
    )
    .join('');
}

function displayHtml(display: DisplayAdminData | undefined, owner: boolean): string {
  if (!display) return '<p class="muted">Could not load the display settings. Try again in a moment.</p>';
  const { settings, link, venues } = display;
  const linkBox = link
    ? `<p class="muted" style="margin:0">Showing <strong>${esc(link.venue)}</strong>. Open this address in the TV's browser, or scan the code with it:</p>
       <input type="text" id="tvUrl" readonly value="${esc(tvAddress(link.token))}" style="margin-top:0.35rem" />
       <div class="row" style="margin-top:0.5rem">
         <button type="button" id="tvCopyBtn" class="secondary">Copy link</button>
         <button type="button" id="tvOpenBtn" class="secondary">Open display</button>
       </div>
       <div class="tv-qr" style="margin:0.75rem auto;max-width:12rem">${renderGuestLinkQr(tvAddress(link.token))}</div>
       <div id="tvLinkActions" class="row">
         <button type="button" id="tvReplaceBtn" class="ghost">Replace link…</button>
         <button type="button" id="tvRemoveBtn" class="ghost">Remove link…</button>
       </div>
       <p class="muted">Anyone who has this address can watch the board, nothing more. If it gets shared by mistake, replace it: the old address stops working at once.</p>`
    : `<p class="muted" style="margin:0">No TV link yet. Make one, then open it in the TV's browser.</p>
       <label for="tvVenue">Venue to show</label>
       <select id="tvVenue">${venues.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('')}</select>
       <button type="button" id="tvCreateBtn" style="width:100%;margin-top:0.5rem">Create TV link</button>`;
  const look = owner
    ? `<label for="dispTheme">Colours on the TV</label>
       <p class="muted" style="margin:0 0 0.35rem">For the TV screen only. On their own phones, players see the board in the colours they chose for themselves.</p>
       <select id="dispTheme">
         <option value="">Default (Elevated)</option>
         ${THEMES.map((t) => `<option value="${t.id}" ${settings.theme === t.id ? 'selected' : ''}>${esc(t.name)} — ${esc(t.description)}</option>`).join('')}
       </select>
       <label class="checkbox-row" style="margin-top:0.5rem"><input type="checkbox" id="dispPlate" ${settings.logoPlate ? 'checked' : ''} /><span>Light plate behind the logo (for a logo that is hard to see on a dark theme)</span></label>`
    : '';
  return `
    ${linkBox}
    <h3 style="margin:1rem 0 0.25rem">Screens</h3>
    <label for="dispSeconds">Seconds on each screen (5–60)</label>
    <input type="number" id="dispSeconds" min="5" max="60" step="1" value="${settings.screenSeconds}" />
    <label class="checkbox-row" style="margin-top:0.5rem"><input type="checkbox" id="dispTable" ${settings.showTable ? 'checked' : ''} /><span>Ranking table (ranks 1–25, then 26–50 when there are more than 25 players)</span></label>
    <label class="checkbox-row"><input type="checkbox" id="dispPodium" ${settings.showPodium ? 'checked' : ''} /><span>Top 3 podium (needs at least 3 players)</span></label>
    ${look}
    <button type="button" id="dispSaveBtn" class="secondary" style="width:100%;margin-top:0.75rem">Save display settings</button>`;
}

function renderHome(
  root: HTMLElement,
  actions: LeaderboardAdminActions,
  periods: LeaderboardPeriod[],
  locked: LeaderboardPlayerItem[],
  tables: TableRequest[],
  notice: string | undefined,
  error: string | undefined,
  extras: HomeExtras = {},
): void {
  const owner = fullLeaderboardAccess(actions.role);
  const now = Date.now();
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${owner ? 'Leaderboard' : 'Players'}</h1>
      <p class="muted">
        ${
          owner
            ? `A period is a fixed stretch (for example two weeks) in which players' scores add up on one board.
        Its house rules and limits are locked from the moment it starts.`
            : 'Approve the tables that want to play, add walk-up players, and help players with their PINs.'
        }
      </p>
      ${notice ? noteBox(notice) : ''}
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      <button type="button" id="standingsBtn" style="width:100%">View the leaderboard</button>
      ${owner ? '<button type="button" id="newPeriodBtn" class="secondary" style="width:100%;margin-top:0.5rem">New period</button>' : ''}
    </div>

    <div class="card">
      <h2>Tables waiting for approval (<span id="tableCount">${tables.length}</span>)</h2>
      <p class="muted">
        A host has four players signed in and is waiting. Check the table, then approve it — the players' phones open up the moment you
        do. This list refreshes by itself.
      </p>
      <div id="tableRequests">${tablesHtml(tables, now)}</div>
    </div>

    <div class="card">
      <h2>Season results</h2>
      <p class="muted">
        When a season ends, the board stays up as <strong>under review</strong>. Once the results are checked, mark the season
        <strong>final</strong> — the TV and players' phones then show it as the final result, with the season champions.
      </p>
      <div id="seasonList">${seasonsHtml(extras.seasons)}</div>
    </div>

    <div class="card">
      <h2>TV display</h2>
      <p class="muted">The leaderboard on the venue's TV: the ranking pages and the top 3, changing by themselves.</p>
      ${displayHtml(extras.display, owner)}
    </div>

    ${
      owner
        ? `<div class="card">
      <h2>Periods (${periods.length})</h2>
      ${periods.length === 0 ? '<p class="muted">No periods yet.</p>' : periods.map(periodCard).join('')}
    </div>`
        : ''
    }

    <div class="card">
      <h2>Players</h2>
      ${
        locked.length === 0
          ? '<p class="muted">Nobody is locked out right now.</p>'
          : `<p class="muted">Locked out after too many wrong PINs:</p>
             ${locked
               .map(
                 (p) => `
               <div class="row" style="align-items:center;margin-top:0.25rem">
                 <span style="flex:1"><strong>${esc(p.nickname)}</strong></span>
                 <button type="button" class="secondary unlock-btn" data-nickname="${esc(p.nickname)}">Unlock</button>
               </div>`,
               )
               .join('')}`
      }
      <h3 style="margin:1rem 0 0.25rem">Add a player now</h3>
      <p class="muted" style="margin:0">For someone standing at the desk — they are approved straight away. Their PIN starts as ${DEFAULT_PIN} and they choose their own the first time they play, so nobody but them ever knows it. Do it while they are with you.</p>
      <label for="addNick">Nickname</label>
      <input type="text" id="addNick" autocomplete="off" maxlength="20" />
      <button type="button" id="addPlayerBtn" class="secondary" style="width:100%;margin-top:0.5rem">Add player</button>

      <h3 style="margin:1rem 0 0.25rem">Unlock a player / forgotten PIN</h3>
      <label for="playerName">Player nickname</label>
      <input type="text" id="playerName" autocomplete="off" />
      <button type="button" id="unlockBtn" class="secondary" style="width:100%;margin-top:0.5rem">Unlock and reset PIN</button>
      <p class="muted">
        For a locked-out player or one who forgot their PIN. Their PIN becomes ${DEFAULT_PIN}, and they are taken to choose their own before
        they can play — you never choose, type or see a PIN. ${DEFAULT_PIN} only works for a short time, so do this with the player standing
        there; if it runs out, unlock them again.
      </p>
      <div id="deletePlayerBox" style="margin-top:0.75rem">
        <button type="button" id="deletePlayerBtn" class="ghost" style="width:100%">Delete this player…</button>
      </div>
      <p class="muted">
        Leaderboard data is deleted on its own: a period's games and results 90 days after the period ends, and an
        account after 90 days without a game. "Delete this player" removes one player straight away, on request — their
        account, scores and seats. Other players' results stay.
      </p>
    </div>

    <button type="button" id="backBtn" class="ghost" style="width:100%;margin-top:0.5rem">Back</button>
  `;

  const again = (msg?: string, err?: string) => void showHome(root, actions, msg, err);
  const attempt = async (work: () => Promise<string>) => {
    try {
      again(await work());
    } catch (e) {
      if (e instanceof AdminAuthError) return renderSecretPrompt(root, actions, 'Incorrect admin secret.');
      again(undefined, e instanceof Error ? e.message : 'That did not work.');
    }
  };

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#newPeriodBtn')?.addEventListener('click', () => void showPeriodForm(root, actions));
  root.querySelector<HTMLButtonElement>('#standingsBtn')!.addEventListener('click', () => void showStandings(root, actions));

  // The tables list: approve / decline, and it refreshes itself so a table that has just been sent appears without reloading.
  const tableBox = root.querySelector<HTMLElement>('#tableRequests')!;
  const wireTables = () => {
    tableBox.querySelectorAll<HTMLButtonElement>('.table-approve').forEach((btn) =>
      btn.addEventListener('click', () => {
        btn.disabled = true;
        void attempt(async () => `${await approveTable(btn.dataset.ref!)} is approved — the players' screens are opening up.`);
      }),
    );
    tableBox.querySelectorAll<HTMLButtonElement>('.table-decline').forEach((btn) =>
      btn.addEventListener('click', () => {
        btn.disabled = true;
        void attempt(async () => `${await declineTable(btn.dataset.ref!)} was declined — the host can take the table back and send it again.`);
      }),
    );
  };
  wireTables();
  const refresher = setInterval(() => {
    if (!root.contains(tableBox)) return clearInterval(refresher);
    fetchTableRequests()
      .then((list) => {
        if (!root.contains(tableBox)) return;
        tableBox.innerHTML = tablesHtml(list, Date.now());
        const count = root.querySelector<HTMLElement>('#tableCount');
        if (count) count.textContent = String(list.length);
        wireTables();
      })
      .catch(() => undefined); // no signal for a moment: keep what is on screen
  }, 5000);
  root.querySelector<HTMLButtonElement>('#addPlayerBtn')!.addEventListener('click', () => {
    const name = root.querySelector<HTMLInputElement>('#addNick')!.value.trim();
    if (!name) return again(undefined, 'Enter the player’s nickname.');
    void attempt(async () => `${await addLeaderboardPlayer(name)} was added. Their PIN is ${DEFAULT_PIN} for now — they choose their own PIN the first time they play.`);
  });

  // Season results: marking final is a two-step (the whole venue sees it), and "back to review" is one tap.
  root.querySelectorAll<HTMLButtonElement>('.season-final').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.id!;
      btn.outerHTML = `
        <span class="muted" style="flex-basis:100%">The TV and every player's phone will show this season as FINAL.</span>
        <button type="button" class="confirm-final-btn" data-id="${esc(id)}">Yes, mark as final</button>
        <button type="button" class="ghost cancel-final-btn">Cancel</button>`;
      root.querySelector<HTMLButtonElement>('.confirm-final-btn')!.addEventListener('click', () =>
        void attempt(async () => {
          await setSeasonFinal(id, true);
          return 'The season is marked as final.';
        }),
      );
      root.querySelector<HTMLButtonElement>('.cancel-final-btn')!.addEventListener('click', () => again());
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('.season-reopen').forEach((btn) =>
    btn.addEventListener('click', () =>
      void attempt(async () => {
        await setSeasonFinal(btn.dataset.id!, false);
        return 'The season is back under review.';
      }),
    ),
  );

  // TV display: make / replace / remove the link, and save the screen settings.
  root.querySelector<HTMLButtonElement>('#tvCreateBtn')?.addEventListener('click', () => {
    const venue = root.querySelector<HTMLSelectElement>('#tvVenue')!.value;
    void attempt(async () => {
      await createTvLinkApi(venue);
      return 'The TV link is ready. Open it in the TV’s browser.';
    });
  });
  root.querySelector<HTMLButtonElement>('#tvCopyBtn')?.addEventListener('click', (e) => {
    const input = root.querySelector<HTMLInputElement>('#tvUrl')!;
    const btn = e.currentTarget as HTMLButtonElement;
    const done = () => (btn.textContent = 'Copied');
    if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(input.value).then(done, () => input.select());
    else input.select();
  });
  root.querySelector<HTMLButtonElement>('#tvOpenBtn')?.addEventListener('click', () => {
    window.open(root.querySelector<HTMLInputElement>('#tvUrl')!.value, '_blank', 'noopener');
  });
  const confirmTv = (btnId: string, message: string, confirmLabel: string, work: () => Promise<string>) => {
    root.querySelector<HTMLButtonElement>(btnId)?.addEventListener('click', () => {
      const box = root.querySelector<HTMLElement>('#tvLinkActions')!;
      box.innerHTML = `
        <span class="muted" style="flex-basis:100%">${esc(message)}</span>
        <button type="button" class="danger" id="tvConfirmBtn">${esc(confirmLabel)}</button>
        <button type="button" class="ghost" id="tvCancelBtn">Cancel</button>`;
      box.querySelector<HTMLButtonElement>('#tvConfirmBtn')!.addEventListener('click', () => void attempt(work));
      box.querySelector<HTMLButtonElement>('#tvCancelBtn')!.addEventListener('click', () => again());
    });
  };
  confirmTv('#tvReplaceBtn', 'The address now on the TV stops working — you will have to open the new one there.', 'Yes, replace', async () => {
    const venue = extras.display?.link?.venue ?? extras.display?.venues[0] ?? '';
    await createTvLinkApi(venue);
    return 'A new TV link was made. The old one no longer works.';
  });
  confirmTv('#tvRemoveBtn', 'The TV will stop showing the board until you make a new link.', 'Yes, remove', async () => {
    await removeTvLinkApi();
    return 'The TV link was removed.';
  });
  root.querySelector<HTMLButtonElement>('#dispSaveBtn')?.addEventListener('click', () => {
    const settings = extras.display!.settings;
    const value = (id: string) => root.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
    void attempt(async () => {
      await saveDisplayAdmin({
        screenSeconds: Number(value('dispSeconds')!.value),
        showTable: (value('dispTable') as HTMLInputElement).checked,
        showPodium: (value('dispPodium') as HTMLInputElement).checked,
        theme: owner ? (value('dispTheme')!.value || null) : settings.theme,
        logoPlate: owner ? (value('dispPlate') as HTMLInputElement).checked : settings.logoPlate,
      });
      return 'Display settings saved.';
    });
  });

  root.querySelectorAll<HTMLButtonElement>('.review-btn').forEach((btn) =>
    btn.addEventListener('click', () => void showReview(root, actions, btn.dataset.id!)),
  );

  // Two-step delete: the first tap swaps the button for a confirmation, so it can't be done by accident.
  root.querySelectorAll<HTMLButtonElement>('.delete-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.id!;
      btn.outerHTML = `
        <button type="button" class="danger confirm-delete-btn" data-id="${esc(id)}">Yes, delete</button>
        <button type="button" class="ghost cancel-delete-btn">Cancel</button>`;
      root.querySelector<HTMLButtonElement>('.confirm-delete-btn')!.addEventListener('click', () =>
        void attempt(async () => {
          await deleteLeaderboardPeriod(id);
          return 'Period deleted.';
        }),
      );
      root.querySelector<HTMLButtonElement>('.cancel-delete-btn')!.addEventListener('click', () => again());
    }),
  );

  root.querySelectorAll<HTMLButtonElement>('.unlock-btn').forEach((btn) =>
    btn.addEventListener('click', () => void attempt(async () => unlockedMessage(await unlockLeaderboardPlayer(btn.dataset.nickname!)))),
  );
  root.querySelector<HTMLButtonElement>('#unlockBtn')!.addEventListener('click', () => {
    const name = root.querySelector<HTMLInputElement>('#playerName')!.value.trim();
    if (!name) return again(undefined, 'Enter the player’s nickname.');
    void attempt(async () => unlockedMessage(await unlockLeaderboardPlayer(name)));
  });

  // Two-step, like deleting a period: the first tap asks, the second does it.
  root.querySelector<HTMLButtonElement>('#deletePlayerBtn')!.addEventListener('click', () => {
    const name = root.querySelector<HTMLInputElement>('#playerName')!.value.trim();
    if (!name) return again(undefined, 'Enter the player’s nickname.');
    const box = root.querySelector<HTMLElement>('#deletePlayerBox')!;
    box.innerHTML = `
      <div class="error-box">Delete <strong>${esc(name)}</strong> and all their leaderboard data? This cannot be undone.</div>
      <div class="row" style="margin-top:0.5rem">
        <button type="button" id="confirmDeletePlayerBtn" class="danger">Yes, delete</button>
        <button type="button" id="cancelDeletePlayerBtn" class="ghost">Cancel</button>
      </div>`;
    box.querySelector<HTMLButtonElement>('#confirmDeletePlayerBtn')!.addEventListener('click', () =>
      void attempt(async () => `${await deleteLeaderboardPlayer(name)} and their leaderboard data were deleted.`),
    );
    box.querySelector<HTMLButtonElement>('#cancelDeletePlayerBtn')!.addEventListener('click', () => again());
  });
}

// --- the new-period form ----------------------------------------------------

async function showPeriodForm(root: HTMLElement, actions: LeaderboardAdminActions, error?: string): Promise<void> {
  loading(root, 'New period');
  await guarded(
    root,
    actions,
    async () => {
      const venues = (await fetchVenues()).map((v) => v.name);
      // "default" is the shared board for devices with no venue — the only one on a server with none configured.
      const choices = [...venues, DEFAULT_VENUE_NAME];
      draft ??= defaultPeriodDraft(Date.now(), choices[0]!);
      renderPeriodForm(root, actions, choices, error);
    },
    (message) => void showHome(root, actions, undefined, message),
  );
}

function readDraft(root: HTMLElement): void {
  if (!draft) return;
  const value = (id: string) => root.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value ?? '';
  draft.venue = value('venueSel');
  draft.startsAt = value('startsAt');
  draft.days = value('days');
  draft.minRounds = value('minRounds');
  draft.maxGamesPerDay = value('maxGamesPerDay');
  draft.maxGamesPerPeriod = value('maxGamesPerPeriod');
  draft.minSecondsPerRound = value('minSecondsPerRound');
  const chosen = root.querySelector<HTMLInputElement>('input[name="settlement"]:checked');
  if (chosen) draft.settlement = chosen.value as Settlement;
}

function renderPeriodForm(root: HTMLElement, actions: LeaderboardAdminActions, venues: string[], error: string | undefined): void {
  const d = draft!;
  const customised = JSON.stringify(d.rules) !== JSON.stringify(defaultHouseRules());
  const styleRow = (s: Settlement) => `
    <label class="checkbox-row rule-row">
      <input type="radio" name="settlement" value="${s}" ${d.settlement === s ? 'checked' : ''} />
      <span>${esc(SETTLEMENT_LABEL[s])}<span class="rule-description muted">${esc(SETTLEMENT_HELP[s])}</span></span>
    </label>`;

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>New leaderboard period</h1>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="venueSel">Venue</label>
      <select id="venueSel">${venues.map((v) => `<option value="${esc(v)}" ${v === d.venue ? 'selected' : ''}>${esc(v)}</option>`).join('')}</select>

      <label for="startsAt">Starts</label>
      <input type="datetime-local" id="startsAt" value="${esc(d.startsAt)}" />

      <label for="days">Length in days</label>
      <div class="row" style="align-items:center">
        <input type="number" id="days" min="1" max="90" step="1" value="${esc(d.days)}" style="flex:1" />
        <button type="button" class="secondary days-btn" data-days="7">1 week</button>
        <button type="button" class="secondary days-btn" data-days="14">2 weeks</button>
      </div>

      <label>Scoring style</label>
      <div class="stack">${styleRow('winner_only')}${styleRow('zero_sum')}</div>

      <label>House rules</label>
      <p class="muted">
        ${customised ? 'Customised' : 'The app’s defaults'} — the ${SETTLEMENT_MODE[d.settlement] === 'fun' ? 'Fun' : 'Serious'} mode settings.
        Players can't change them.
      </p>
      <button type="button" id="rulesBtn" class="secondary" style="width:100%">Set house rules…</button>

      <h2>Limits</h2>
      <label for="maxGamesPerDay">Games per player, per 24 hours</label>
      <input type="number" id="maxGamesPerDay" min="1" max="50" step="1" value="${esc(d.maxGamesPerDay)}" />
      <label for="maxGamesPerPeriod">Games per player, whole period (blank = no limit)</label>
      <input type="number" id="maxGamesPerPeriod" min="1" max="1000" step="1" value="${esc(d.maxGamesPerPeriod)}" />
      <label for="minRounds">Fewest rounds for a game to count</label>
      <input type="number" id="minRounds" min="1" max="200" step="1" value="${esc(d.minRounds)}" />
      <label for="minSecondsPerRound">Fastest a round can plausibly be, in seconds (0 = off)</label>
      <input type="number" id="minSecondsPerRound" min="0" max="600" step="1" value="${esc(d.minSecondsPerRound)}" />
      <p class="muted">
        All of this is locked once the period starts. If you get it wrong, delete the period before its start time and make a new one.
      </p>

      <div class="row" style="margin-top:1rem">
        <button type="button" id="cancelBtn" class="secondary">Cancel</button>
        <button type="button" id="createBtn">Create period</button>
      </div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('.days-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      root.querySelector<HTMLInputElement>('#days')!.value = btn.dataset.days!;
    }),
  );
  // Switching style changes which mode's rules the period carries, so redraw (the summary line names it).
  root.querySelectorAll<HTMLInputElement>('input[name="settlement"]').forEach((radio) =>
    radio.addEventListener('change', () => {
      readDraft(root);
      renderPeriodForm(root, actions, venues, undefined);
    }),
  );
  root.querySelector<HTMLButtonElement>('#cancelBtn')!.addEventListener('click', () => {
    draft = undefined;
    void showHome(root, actions);
  });
  root.querySelector<HTMLButtonElement>('#rulesBtn')!.addEventListener('click', () => {
    readDraft(root);
    const mode = SETTLEMENT_MODE[draft!.settlement];
    renderSettings(root, mode, draft!.rules, undefined, {
      save: (overrides) => {
        draft!.rules = overrides;
        renderPeriodForm(root, actions, venues, undefined);
      },
      cancel: () => renderPeriodForm(root, actions, venues, undefined),
      leaderboardRules: true,
    });
  });
  root.querySelector<HTMLButtonElement>('#createBtn')!.addEventListener('click', () => {
    readDraft(root);
    const built = buildPeriodRequest(draft!);
    if (!built.ok) return renderPeriodForm(root, actions, venues, built.error);
    void (async () => {
      try {
        await createLeaderboardPeriod(built.input);
        draft = undefined;
        void showHome(root, actions, 'Period created.');
      } catch (e) {
        if (e instanceof AdminAuthError) return renderSecretPrompt(root, actions, 'Incorrect admin secret.');
        renderPeriodForm(root, actions, venues, e instanceof Error ? e.message : 'Could not create the period.');
      }
    })();
  });
}

// --- the review list --------------------------------------------------------

async function showReview(root: HTMLElement, actions: LeaderboardAdminActions, periodId: string, flaggedOnly?: boolean, notice?: string): Promise<void> {
  loading(root, 'Review games');
  await guarded(
    root,
    actions,
    async () => {
      const review = await fetchPeriodReview(periodId);
      renderReview(root, actions, periodId, review, flaggedOnly ?? review.summary.flagged > 0, notice, undefined);
    },
    (message) => void showHome(root, actions, undefined, message),
  );
}

function gameCard(g: ReviewGameItem): string {
  const seats = g.players
    .map(
      (p) => `
      <div class="lb-seat">
        <span class="lb-name">${esc(p.nickname)}${p.counted ? '' : ' <span class="tag">not counted</span>'}</span>
        <span>${p.points === null ? '—' : esc(formatDelta(p.points))}</span>
        <span class="muted">${p.wins ?? 0} win${p.wins === 1 ? '' : 's'}</span>
      </div>`,
    )
    .join('');
  const flags = g.flags
    .map((f) => `<div class="lb-flag ${f.severity}">${f.severity === 'warn' ? 'Look closer: ' : 'Note: '}${esc(f.message)}</div>`)
    .join('');
  const hidden = g.hidden
    ? `<div class="lb-note">Hidden${g.hiddenAt ? ` on ${esc(new Date(g.hiddenAt).toLocaleDateString())}` : ''}: ${esc(g.hiddenReason ?? 'no reason recorded')}</div>`
    : '';
  const action = g.hidden
    ? `<button type="button" class="secondary restore-btn" data-id="${esc(g.id)}">Restore</button>`
    : `<button type="button" class="ghost hide-btn" data-id="${esc(g.id)}">Hide this game…</button>`;
  return `
    <div class="card lb-game ${g.hidden ? 'lb-hidden' : ''}" style="margin-top:0.5rem">
      <div><strong>${esc(new Date(g.completedAt).toLocaleString())}</strong></div>
      <div class="muted">${esc(formatDuration(g.durationMs))} · ${g.rounds ?? '?'} rounds${g.deviceId ? ` · phone ${esc(g.deviceId.slice(0, 6))}` : ''}</div>
      ${flags}
      ${seats}
      ${hidden}
      <div class="lb-hide-form" data-id="${esc(g.id)}" style="display:none;margin-top:0.5rem">
        <input type="text" class="hide-reason" maxlength="200" placeholder="Why? e.g. same four farming points" />
        <div class="row" style="margin-top:0.4rem">
          <button type="button" class="ghost cancel-hide-btn" data-id="${esc(g.id)}">Cancel</button>
          <button type="button" class="danger confirm-hide-btn" data-id="${esc(g.id)}">Hide game</button>
        </div>
      </div>
      <div class="row" style="margin-top:0.5rem">${action}</div>
    </div>`;
}

function renderReview(
  root: HTMLElement,
  actions: LeaderboardAdminActions,
  periodId: string,
  review: PeriodReviewData,
  flaggedOnly: boolean,
  notice: string | undefined,
  error: string | undefined,
): void {
  const p = review.period;
  const shown = sortReviewGames(review.games).filter((g) => !flaggedOnly || g.flags.length > 0 || g.hidden);
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Review games</h1>
      <div><strong>${esc(p.venue)}</strong></div>
      <div class="muted">${esc(formatPeriodRange(p))} · ${esc(SETTLEMENT_LABEL[p.settlement])}</div>
      <div style="margin-top:0.5rem">
        ${review.summary.games} game${review.summary.games === 1 ? '' : 's'} ·
        ${review.summary.flagged} with warnings · ${review.summary.hidden} hidden
      </div>
      ${notice ? noteBox(notice) : ''}
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      <label class="checkbox-row" style="margin-top:0.5rem">
        <input type="checkbox" id="flaggedOnly" ${flaggedOnly ? 'checked' : ''} />
        Show only games with a warning or note
      </label>
      <p class="muted">
        A warning means "worth a look", not proof — the app can't see the table. Hiding a game takes it out of every
        total straight away and gives the players their game allowance back; you can restore it at any time.
      </p>
    </div>
    ${
      shown.length === 0
        ? `<div class="card" style="margin-top:0.5rem"><p class="muted">${
            review.summary.games === 0 ? 'No finished games in this period yet.' : 'No games with warnings.'
          }</p></div>`
        : shown.map(gameCard).join('')
    }
    <button type="button" id="backBtn" class="ghost" style="width:100%;margin-top:0.5rem">Back to Leaderboard</button>
  `;

  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => void showHome(root, actions));
  root.querySelector<HTMLInputElement>('#flaggedOnly')!.addEventListener('change', (e) => {
    renderReview(root, actions, periodId, review, (e.target as HTMLInputElement).checked, undefined, undefined);
  });

  const reload = (msg: string) => void showReview(root, actions, periodId, flaggedOnly, msg);
  const attempt = async (work: () => Promise<void>, doneMessage: string) => {
    try {
      await work();
      reload(doneMessage);
    } catch (e) {
      if (e instanceof AdminAuthError) return renderSecretPrompt(root, actions, 'Incorrect admin secret.');
      renderReview(root, actions, periodId, review, flaggedOnly, undefined, e instanceof Error ? e.message : 'That did not work.');
    }
  };

  root.querySelectorAll<HTMLButtonElement>('.hide-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      const form = root.querySelector<HTMLElement>(`.lb-hide-form[data-id="${btn.dataset.id}"]`)!;
      form.style.display = '';
      btn.style.display = 'none';
      form.querySelector<HTMLInputElement>('.hide-reason')!.focus();
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('.cancel-hide-btn').forEach((btn) =>
    btn.addEventListener('click', () => renderReview(root, actions, periodId, review, flaggedOnly, undefined, undefined)),
  );
  root.querySelectorAll<HTMLButtonElement>('.confirm-hide-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      const form = root.querySelector<HTMLElement>(`.lb-hide-form[data-id="${btn.dataset.id}"]`)!;
      const reason = form.querySelector<HTMLInputElement>('.hide-reason')!.value.trim();
      if (!reason) {
        return renderReview(root, actions, periodId, review, flaggedOnly, undefined, 'Give a short reason so the record shows why.');
      }
      void attempt(() => hideLeaderboardGame(btn.dataset.id!, reason), 'Game hidden — the totals were recalculated.');
    }),
  );
  root.querySelectorAll<HTMLButtonElement>('.restore-btn').forEach((btn) =>
    btn.addEventListener('click', () =>
      void attempt(() => restoreLeaderboardGame(btn.dataset.id!), 'Game restored — the totals were recalculated.'),
    ),
  );
}
